import React from 'react';
import { useMutation, UseMutationResult, UseMutationOptions } from '@tanstack/react-query';
import service_people from 'services/people';
import { useParams } from 'react-router-dom';

/**
 * @typedef {boolean} UseCompletelySwitchOptInMutationVariables
 */

/**
 * @typedef {Omit<UseMutationOptions<Object, Error, UseCompletelySwitchOptInMutationVariables, any>, 'mutationKey' | 'mutationFn'>} UseCompletelySwitchOptInMutationOptions
 */

/**
 * A custom hook for triggering a mutation that completely switches opt-in consent for a person on a specified person.
 *
 * @param {string} accountUuid - The UUID of the account for which the opt-in consent should be switched.
 * @param {string} personUuid - The UUID of the person whose opt-in consent is being modified.
 * @param {UseCompletelySwitchOptInMutationOptions} [options={}] - Optional configuration parameters to customize the behavior of the mutation.
 * @return {UseMutationResult<Object, Error, UseCompletelySwitchOptInMutationVariables, unknown>} The mutation instance provided by the `useMutation` hook.
 */
export const useCompletelySwitchOptInMutation = (accountUuid, personUuid, options = {}) => {
    return useMutation({
        ...options,
        mutationKey: ['completely-switch-opt-in'],
        mutationFn: value => service_people.consentOptIn(accountUuid, personUuid, value)
    });
};

/**
 * This HOC provides the `completelySwitchOptInMutation` prop to the specified component, which can then be
 * used to handle state changes and operations related to opting in or out within the provided options context.
 *
 * @param {React.ComponentType} Component - The component to be wrapped and enhanced with the mutation prop.
 * @param {Object} options - Configuration options used for initializing the mutation.
 * @returns {React.ComponentType} A new component with the `completelySwitchOptInMutation` prop injected.
 */
export const withCompletelySwitchOptInMutation = (Component, options) => props => {
    const { account_uuid: accountUuid, person_uuid: personUuid } = useParams();
    const completelySwitchOptInMutation = useCompletelySwitchOptInMutation(accountUuid, personUuid, options);

    return <Component {...props} completelySwitchOptInMutation={completelySwitchOptInMutation} />;
};
